/**
 * Capacity Planning Template Form
 * Team capacity & allocation planning
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const CapacityPlanningForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    company_name: '',
    department: '',
    period: 'Q1 2025',
    date: new Date().toISOString().split('T')[0],
    prepared_by: '',
    team_capacity: [{
      name: '', role: '', department: '', total_hours: 160, allocated_hours: 0,
      available_hours: 160, utilization: 0, projects: '', skills: '', notes: ''
    }],
    project_allocations: [{
      project_name: '', start_date: '', end_date: '', required_hours: 0,
      allocated_hours: 0, team_members: '', status: 'Planned', priority: 'Medium', notes: ''
    }],
    total_capacity_hours: 0,
    total_allocated_hours: 0,
    utilization_target: 80.0,
    notes: '',
    rai_assignments: [],
    followups: []
  });

  const getSampleData = () => ({
    company_name: 'TechCorp Inc.',
    department: 'Engineering',
    period: 'Q1 2025',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'Engineering Manager',
    team_capacity: [
      { name: 'John Smith', role: 'Senior Developer', department: 'Engineering', total_hours: 480, allocated_hours: 420, available_hours: 60, utilization: 87.5, projects: 'Platform V2, API Gateway', skills: 'Python, AWS, React', notes: 'Tech lead on Platform V2' },
      { name: 'Jane Doe', role: 'Full Stack Developer', department: 'Engineering', total_hours: 480, allocated_hours: 400, available_hours: 80, utilization: 83.3, projects: 'Platform V2, Mobile App', skills: 'React, Node.js, PostgreSQL', notes: '' },
      { name: 'Mike Wilson', role: 'DevOps Engineer', department: 'Engineering', total_hours: 480, allocated_hours: 360, available_hours: 120, utilization: 75.0, projects: 'Infrastructure, CI/CD', skills: 'Kubernetes, Terraform, AWS', notes: 'Available for new projects' },
      { name: 'Sarah Chen', role: 'QA Engineer', department: 'QA', total_hours: 480, allocated_hours: 440, available_hours: 40, utilization: 91.7, projects: 'Platform V2, API Gateway, Mobile App', skills: 'Selenium, Cypress, Performance Testing', notes: 'Heavy load Q1' },
      { name: 'Alex Kumar', role: 'Junior Developer', department: 'Engineering', total_hours: 480, allocated_hours: 320, available_hours: 160, utilization: 66.7, projects: 'Mobile App', skills: 'React Native, JavaScript', notes: 'Ramping up' }
    ],
    project_allocations: [
      { project_name: 'Platform V2', start_date: '2025-01-01', end_date: '2025-03-31', required_hours: 1200, allocated_hours: 1100, team_members: 'John Smith, Jane Doe, Sarah Chen', status: 'In Progress', priority: 'Critical', notes: 'Major release Q1' },
      { project_name: 'API Gateway', start_date: '2025-01-15', end_date: '2025-02-28', required_hours: 400, allocated_hours: 400, team_members: 'John Smith, Sarah Chen', status: 'Planned', priority: 'High', notes: 'Dependency for Platform V2' },
      { project_name: 'Mobile App', start_date: '2025-02-01', end_date: '2025-03-31', required_hours: 600, allocated_hours: 520, team_members: 'Jane Doe, Alex Kumar, Sarah Chen', status: 'Planned', priority: 'Medium', notes: 'v2.0 redesign' },
      { project_name: 'Infrastructure Upgrade', start_date: '2025-01-01', end_date: '2025-03-31', required_hours: 360, allocated_hours: 360, team_members: 'Mike Wilson', status: 'In Progress', priority: 'High', notes: 'Kubernetes migration' }
    ],
    total_capacity_hours: 2400,
    total_allocated_hours: 1940,
    utilization_target: 80.0,
    notes: 'Q1 is heavily loaded due to Platform V2 release. Consider hiring additional QA resource or contractor support for Mobile App testing.',
    rai_assignments: [
      { task: 'Review capacity plan monthly', responsible: 'Engineering Manager', accountable: 'VP Engineering', informed: 'PMO, HR', due_date: '2025-01-31', status: 'Pending' }
    ],
    followups: [
      { item: 'Hire additional QA contractor', owner: 'HR Manager', due_date: '2025-01-20', priority: 'High', status: 'In Progress' },
      { item: 'Finalize Platform V2 team allocation', owner: 'Engineering Manager', due_date: '2025-01-10', priority: 'Critical', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Capacity Planning Draft";
      const payload = {
        name: draftName,
        template_type: "Capacity Planning",
        template_id: "capacity-planning",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "capacity-planning", "Capacity Planning", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("capacity-planning", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/capacity-planning`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Capacity_Planning_${formData.department.replace(/\s+/g, '_') || 'Team'}_${formData.period.replace(/\s+/g, '_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Capacity Planning",
        formData,
        settings: activeSettings || {},
        templateType: "Capacity Planning",
        templateId: "capacity-planning",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addTeamMember = () => {
    setFormData({
      ...formData,
      team_capacity: [...formData.team_capacity, {
        name: '', role: '', department: formData.department, total_hours: 160, allocated_hours: 0,
        available_hours: 160, utilization: 0, projects: '', skills: '', notes: ''
      }]
    });
  };

  const updateTeamMember = (index, field, value) => {
    const team = [...formData.team_capacity];
    team[index] = { ...team[index], [field]: value };
    // Auto-calculate utilization and available hours
    if (field === 'total_hours' || field === 'allocated_hours') {
      const total = parseFloat(team[index].total_hours) || 0;
      const allocated = parseFloat(team[index].allocated_hours) || 0;
      team[index].available_hours = total - allocated;
      team[index].utilization = total > 0 ? ((allocated / total) * 100).toFixed(1) : 0;
    }
    setFormData({ ...formData, team_capacity: team });
  };

  const removeTeamMember = (index) => {
    setFormData({ ...formData, team_capacity: formData.team_capacity.filter((_, i) => i !== index) });
  };

  const addProject = () => {
    setFormData({
      ...formData,
      project_allocations: [...formData.project_allocations, {
        project_name: '', start_date: '', end_date: '', required_hours: 0,
        allocated_hours: 0, team_members: '', status: 'Planned', priority: 'Medium', notes: ''
      }]
    });
  };

  const updateProject = (index, field, value) => {
    const projects = [...formData.project_allocations];
    projects[index] = { ...projects[index], [field]: value };
    setFormData({ ...formData, project_allocations: projects });
  };

  const removeProject = (index) => {
    setFormData({ ...formData, project_allocations: formData.project_allocations.filter((_, i) => i !== index) });
  };

  const roles = ['Senior Developer', 'Developer', 'Junior Developer', 'Tech Lead', 'DevOps Engineer', 'QA Engineer', 'Designer', 'Product Manager', 'Project Manager', 'Architect', 'Other'];
  const statuses = ['Planned', 'In Progress', 'On Hold', 'Completed', 'Cancelled'];
  const priorities = ['Critical', 'High', 'Medium', 'Low'];

  // Calculate totals
  const totalCapacity = formData.team_capacity.reduce((sum, m) => sum + (parseFloat(m.total_hours) || 0), 0);
  const totalAllocated = formData.team_capacity.reduce((sum, m) => sum + (parseFloat(m.allocated_hours) || 0), 0);
  const overallUtilization = totalCapacity > 0 ? ((totalAllocated / totalCapacity) * 100).toFixed(1) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Capacity Planning</h1>
                <p className="text-sm text-gray-500">Team capacity & allocation planning</p>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Planning Information</CardTitle>
            <div className="flex gap-4 mt-2">
              <div className="bg-blue-100 rounded-lg px-4 py-2 text-center">
                <div className="text-xs text-gray-600">Total Capacity</div>
                <div className="text-xl font-bold text-blue-700">{totalCapacity}h</div>
              </div>
              <div className="bg-green-100 rounded-lg px-4 py-2 text-center">
                <div className="text-xs text-gray-600">Allocated</div>
                <div className="text-xl font-bold text-green-700">{totalAllocated}h</div>
              </div>
              <div className={`rounded-lg px-4 py-2 text-center ${parseFloat(overallUtilization) > 85 ? 'bg-red-100' : parseFloat(overallUtilization) > 70 ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                <div className="text-xs text-gray-600">Utilization</div>
                <div className={`text-xl font-bold ${parseFloat(overallUtilization) > 85 ? 'text-red-700' : parseFloat(overallUtilization) > 70 ? 'text-yellow-700' : 'text-gray-700'}`}>{overallUtilization}%</div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div><Label>Company *</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Company" /></div>
              <div><Label>Department *</Label><Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} placeholder="Engineering" /></div>
              <div><Label>Period</Label><Input value={formData.period} onChange={(e) => setFormData({...formData, period: e.target.value})} placeholder="Q1 2025" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="Manager" /></div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Team Capacity</Label>
              <p className="text-xs text-gray-500 mb-3">Hours are for the entire period (e.g., 480h = 3 months at 160h/month)</p>
              
              <div className="space-y-3">
                {formData.team_capacity.map((member, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg border">
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                      <div><Label className="text-xs">Name</Label><Input value={member.name} onChange={(e) => updateTeamMember(idx, 'name', e.target.value)} placeholder="Name" className="text-sm" /></div>
                      <div><Label className="text-xs">Role</Label>
                        <Select value={member.role} onValueChange={(v) => updateTeamMember(idx, 'role', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue placeholder="Role" /></SelectTrigger>
                          <SelectContent>{roles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Total Hours</Label><Input type="number" value={member.total_hours} onChange={(e) => updateTeamMember(idx, 'total_hours', parseFloat(e.target.value) || 0)} className="text-sm" /></div>
                      <div><Label className="text-xs">Allocated Hours</Label><Input type="number" value={member.allocated_hours} onChange={(e) => updateTeamMember(idx, 'allocated_hours', parseFloat(e.target.value) || 0)} className="text-sm" /></div>
                      <div><Label className="text-xs">Available</Label><Input type="number" value={member.available_hours} readOnly className="text-sm bg-gray-100" /></div>
                      <div className="flex items-end gap-1">
                        <div className="flex-1"><Label className="text-xs">Util %</Label><Input type="number" value={member.utilization} readOnly className={`text-sm ${parseFloat(member.utilization) > 85 ? 'bg-red-100' : parseFloat(member.utilization) > 70 ? 'bg-yellow-100' : 'bg-green-100'}`} /></div>
                        <Button variant="ghost" size="icon" onClick={() => removeTeamMember(idx)} className="h-9"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                      <div><Label className="text-xs">Projects</Label><Input value={member.projects} onChange={(e) => updateTeamMember(idx, 'projects', e.target.value)} placeholder="Project A, Project B" className="text-sm" /></div>
                      <div><Label className="text-xs">Skills</Label><Input value={member.skills} onChange={(e) => updateTeamMember(idx, 'skills', e.target.value)} placeholder="React, Python, AWS" className="text-sm" /></div>
                      <div><Label className="text-xs">Notes</Label><Input value={member.notes} onChange={(e) => updateTeamMember(idx, 'notes', e.target.value)} placeholder="Notes" className="text-sm" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addTeamMember} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Team Member</Button>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Project Allocations</Label>
              <div className="space-y-3 mt-3">
                {formData.project_allocations.map((project, idx) => (
                  <div key={idx} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      <div><Label className="text-xs">Project Name</Label><Input value={project.project_name} onChange={(e) => updateProject(idx, 'project_name', e.target.value)} placeholder="Project name" className="text-sm" /></div>
                      <div><Label className="text-xs">Start Date</Label><Input type="date" value={project.start_date} onChange={(e) => updateProject(idx, 'start_date', e.target.value)} className="text-sm" /></div>
                      <div><Label className="text-xs">End Date</Label><Input type="date" value={project.end_date} onChange={(e) => updateProject(idx, 'end_date', e.target.value)} className="text-sm" /></div>
                      <div><Label className="text-xs">Required Hours</Label><Input type="number" value={project.required_hours} onChange={(e) => updateProject(idx, 'required_hours', parseFloat(e.target.value) || 0)} className="text-sm" /></div>
                      <div className="flex items-end gap-1">
                        <div className="flex-1"><Label className="text-xs">Allocated</Label><Input type="number" value={project.allocated_hours} onChange={(e) => updateProject(idx, 'allocated_hours', parseFloat(e.target.value) || 0)} className="text-sm" /></div>
                        <Button variant="ghost" size="icon" onClick={() => removeProject(idx)} className="h-9"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                      <div><Label className="text-xs">Team Members</Label><Input value={project.team_members} onChange={(e) => updateProject(idx, 'team_members', e.target.value)} placeholder="John, Jane" className="text-sm" /></div>
                      <div><Label className="text-xs">Status</Label>
                        <Select value={project.status} onValueChange={(v) => updateProject(idx, 'status', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Priority</Label>
                        <Select value={project.priority} onValueChange={(v) => updateProject(idx, 'priority', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Notes</Label><Input value={project.notes} onChange={(e) => updateProject(idx, 'notes', e.target.value)} placeholder="Notes" className="text-sm" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addProject} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Project</Button>
            </div>

            <Separator />

            <div><Label>Additional Notes</Label><Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Observations, risks, recommendations..." rows={3} /></div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CapacityPlanningForm;
